import React from "react";
import { ChevronLeft, ChevronRight, ImageIcon, Images, X } from "lucide-react";
import { Dialog, DialogClose, DialogContent } from "../ui/dialog";
import { SmartLink } from "../smart-link";
import { ComparisonFrame } from "../before-after/comparison-frame";
import { cn } from "../../lib/utils";
import { imagePositionStyle, type ImagePosition } from "../../lib/image-position";
import { plainText } from "../../lib/plain-text";
import { galleryItemKind, type GalleryItemKind } from "./gallery.kind";
import {
  galleryViewsOf,
  getProjectPhotos,
  photoGroupsOf,
  viewIndexForImage,
  viewIndexForStop,
  type GalleryGroup,
} from "./gallery.groups";
import type { BaseComponentProps } from "../../types/puck";

/** One photo of a project. */
export interface GalleryPhoto {
  imageUrl: string;
  alt?: string;
  caption?: string;
}

/** What a click on a project tile does. */
export type GalleryClickAction = "gallery" | "link";

/**
 * One project in the gallery. `imageUrl` is the cover shown in the grid; `photos` is the
 * rest of that project's shoot, which the lightbox cycles through. Absent or empty
 * `photos` means the project is a single image, which is how every gallery read before
 * projects existed.
 */
export interface GalleryImage {
  /**
   * What this entry is, which decides its settings in the editor and how it
   * renders. Absent on entries saved before the field; those are read from what
   * they hold. See gallery.kind.ts.
   */
  kind?: GalleryItemKind;
  imageUrl: string;
  alt: string;
  caption?: string;
  tag?: string;
  /** Where the photo keeps its focus when the tile crops it. */
  position?: ImagePosition;
  href?: string;
  /**
   * Whether clicking the tile opens this project's photos or follows `href`. Left unset it
   * falls back to the old implicit rule — a tile with a link navigated, one without opened
   * the lightbox — so tiles saved before this field existed keep behaving the same way.
   */
  clickAction?: GalleryClickAction;
  photos?: GalleryPhoto[];
  beforeImageUrl?: string;
  afterImageUrl?: string;
  beforeLabel?: string;
  afterLabel?: string;
  /**
   * Photo-library project this tile follows. While set, the project's current
   * photos are pulled live at render (the reviews-widget pattern), so photos
   * added to the project later show up without re-editing. Unsyncing in the
   * editor materializes the pulled list into `imageUrl`/`photos` and clears
   * this — data is copied on the way out, never deleted.
   */
  sourceProjectId?: string;
}

export interface GalleryProps extends BaseComponentProps {
  heading: string;
  subheading?: string;
  images: GalleryImage[];
  layout?: "grid" | "showcase" | "masonry" | "before-after";
  /** Surface treatment: card chrome with hover lift, or frameless editorial tiles. */
  variant?: "cards" | "editorial";
  columns?: "2" | "3" | "4";
  showCaptions?: "yes" | "no";
  showTags?: "yes" | "no";
  backgroundColor?: string;
  /** From Puck metadata in the editors; window config carries them on live sites. */
  puckSiteId?: string;
  puckApiUrl?: string;
}

/** Live project photos, cached per project for the page view. */
const projectPhotosCache = new Map<string, { photos: GalleryPhoto[]; timestamp: number }>();
const PROJECT_PHOTOS_CACHE_TTL = 5 * 60 * 1000;

export type { GalleryPhotoStop, GalleryGroup, GalleryView } from "./gallery.groups";

/**
 * A comparison needs both sides AND to be one: an entry a pro has set to Photo
 * renders as a photo even if it still carries a stale pair, so hiding those
 * settings in the editor can never leave the tile showing something else.
 * Entries saved before the kind existed are read from what they hold, which is
 * the rule this function used to apply on its own.
 */
function hasComparison(image: GalleryImage): boolean {
  return galleryItemKind(image) === "before-after" && !!image.beforeImageUrl && !!image.afterImageUrl;
}

/** Explicit choice wins; otherwise fall back to the pre-`clickAction` implicit rule. */
function opensLink(image: GalleryImage): boolean {
  if (!image.href) {
    return false;
  }

  return (image.clickAction ?? "link") === "link";
}

function getGridCols(columns: GalleryProps["columns"]): string {
  switch (columns) {
    case "2":
      return "jobsite:md:grid-cols-2";
    case "4":
      return "jobsite:sm:grid-cols-2 jobsite:xl:grid-cols-4";
    case "3":
    default:
      return "jobsite:sm:grid-cols-2 jobsite:lg:grid-cols-3";
  }
}

function getShowcaseFeaturedSpan(columns: GalleryProps["columns"]): string {
  switch (columns) {
    case "3":
    case "4":
      return "jobsite:lg:col-span-2 jobsite:lg:row-span-2";
    case "2":
    default:
      return "";
  }
}

function getMasonryColumnsClass(columns: GalleryProps["columns"]): string {
  switch (columns) {
    case "2":
      return "jobsite:sm:columns-2";
    case "4":
      return "jobsite:sm:columns-2 jobsite:xl:columns-4";
    case "3":
    default:
      return "jobsite:sm:columns-2 jobsite:lg:columns-3";
  }
}

/**
 * Media-box sizing for showcase tiles — the featured one and its siblings alike.
 *
 * At 3 and 4 columns the featured tile spans two grid rows from `lg` up, so the cell is
 * taller than any fixed ratio fills. Keeping an aspect ratio there is actively wrong: with a
 * definite stretched height, `aspect-ratio` computes the box's *width* from that height, so
 * the box grows wider than the card and everything positioned inside it (count badge,
 * caption) is pushed out past the clip. Drop the ratio where the tile spans and fill the cell
 * instead.
 *
 * The siblings need the same treatment for the mirror-image reason. The featured tile's own
 * height comes from its photo's proportions, so the two rows it spans end up taller than a
 * 4/3 box. A sibling card is a `<button>`, which centres its content, so the shortfall shows
 * up as equal bands above and below the photo rather than one gap underneath — which reads as
 * a broken image rather than a short one.
 *
 * WebKit caveat: the featured tile's height must come from an IN-FLOW image at its natural
 * proportions, and sibling images must be absolutely positioned fills. An in-flow `h-full`
 * image inside this auto-height chain is a percentage cycle that Chromium resolves to auto
 * but WebKit resolves to its maximum layout size (33,554,432px), destroying the page.
 *
 * The fill treatment only works for siblings sharing a row with the featured tile — those
 * rows get their height from it. A tile in row 3+ sits in an auto-height row, where an
 * absolutely positioned image contributes nothing and the whole card collapses to a sliver.
 * Overflow tiles keep the fixed ratio instead.
 */
function getShowcaseMediaClass(
  columns: GalleryProps["columns"],
  isFeatured: boolean,
  isOverflow: boolean,
): string {
  if (columns === "3" || columns === "4") {
    if (isFeatured) {
      return "jobsite:aspect-[4/3] jobsite:lg:aspect-auto";
    }
    if (isOverflow) {
      return "jobsite:aspect-[4/3]";
    }
    return "jobsite:aspect-[4/3] jobsite:lg:aspect-auto jobsite:lg:h-full";
  }

  return "jobsite:aspect-[4/3]";
}

/** Image sizing paired with getShowcaseMediaClass; see the WebKit caveat there. */
function getShowcaseImageClass(
  columns: GalleryProps["columns"],
  isFeatured: boolean,
  isOverflow: boolean,
): string {
  if (columns === "3" || columns === "4") {
    if (isFeatured) {
      return "jobsite:h-full jobsite:object-cover jobsite:lg:h-auto";
    }
    if (isOverflow) {
      return "jobsite:h-full jobsite:object-cover";
    }
    return "jobsite:h-full jobsite:object-cover jobsite:lg:absolute jobsite:lg:inset-0 jobsite:lg:h-full jobsite:lg:w-full";
  }

  return "jobsite:h-full jobsite:object-cover";
}

/**
 * Legacy Gallery mode (layout: "before-after"), kept rendering forever for
 * saved rows. The card is chrome around the shared ComparisonFrame, so old
 * galleries get the same on-image interaction as the BeforeAfter component.
 */
function BeforeAfterGalleryCard({
  heading,
  image,
  showCaptions,
  showTags,
  surfaceRadiusStyle,
}: {
  heading: string;
  image: GalleryImage;
  showCaptions: GalleryProps["showCaptions"];
  showTags: GalleryProps["showTags"];
  surfaceRadiusStyle: React.CSSProperties;
}) {
  const beforeImageUrl = image.beforeImageUrl || image.imageUrl;
  const afterImageUrl = image.afterImageUrl || image.imageUrl;
  const beforeLabel = image.beforeLabel || "Before";
  const afterLabel = image.afterLabel || "After";
  const comparisonAlt = image.alt || image.caption || plainText(heading);
  const groupLabel = `Compare ${beforeLabel} and ${afterLabel}${
    image.caption ? `: ${image.caption}` : ""
  }`;

  return (
    <div
      className="jobsite:overflow-hidden jobsite:border jobsite:border-border/70 jobsite:bg-background jobsite:shadow-sm"
      style={surfaceRadiusStyle}
    >
      {beforeImageUrl && afterImageUrl ? (
        <ComparisonFrame
          beforeSrc={beforeImageUrl}
          afterSrc={afterImageUrl}
          beforeAlt={`${beforeLabel}: ${comparisonAlt}`}
          afterAlt={`${afterLabel}: ${comparisonAlt}`}
          beforeLabel={beforeLabel}
          afterLabel={afterLabel}
          groupLabel={groupLabel}
        />
      ) : (
        <div className="jobsite:relative jobsite:aspect-[4/3] jobsite:overflow-hidden">
          <img
            src={beforeImageUrl || afterImageUrl}
            alt={comparisonAlt}
            className="jobsite:h-full jobsite:w-full jobsite:object-cover"
            loading="lazy"
          />
        </div>
      )}

      {(showTags === "yes" && image.tag) || (showCaptions === "yes" && image.caption) ? (
        <div className="jobsite:space-y-2 jobsite:p-4 jobsite:sm:p-5">
          {showTags === "yes" && image.tag ? (
            <span
              className="jobsite:inline-flex jobsite:bg-accent/15 jobsite:px-3 jobsite:py-1 jobsite:text-[11px] jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:text-accent"
              style={surfaceRadiusStyle}
            >
              {image.tag}
            </span>
          ) : null}
          {showCaptions === "yes" && image.caption ? (
            <div className="jobsite:font-heading jobsite:text-xl jobsite:text-foreground">
              {image.caption}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** "1 photo", never "1 photos". */
function photoCountText(count: number): string {
  return `${count} ${count === 1 ? "photo" : "photos"}`;
}

/** What to call a group on the card. A set a pro never named is still a
 *  project; the loose group is just the rest of the photos. */
function groupTitle(group: GalleryGroup): string {
  if (group.label) {
    return group.label;
  }
  return group.kind === "set" ? "Another project" : "More photos";
}

/** The line that closes a group. Named sets say which project ended, unnamed
 *  ones say "this project", and the loose photos are simply photos. A group of
 *  one gets its own wording: "all 1 photo" is not a sentence. */
function endOfGroupText(group: GalleryGroup): string {
  const lead =
    group.stops.length === 1
      ? "That's the only photo"
      : `That's all ${photoCountText(group.stops.length)}`;
  if (group.kind !== "set") {
    return lead;
  }
  return group.label ? `${lead} of ${group.label}` : `${lead} of this project`;
}

/**
 * The stop that closes a group and hands the visitor the next one. Paging used
 * to run straight from one project into the next, so the photos changed
 * subject with nothing to say they had; this says it, and makes going on a
 * choice.
 */
function NextGroupCard({
  group,
  nextGroup,
  onView,
}: {
  group: GalleryGroup;
  nextGroup: GalleryGroup;
  onView: () => void;
}) {
  const cover = nextGroup.stops[0]?.photo.imageUrl;

  return (
    <div className="jobsite:flex jobsite:max-h-full jobsite:w-full jobsite:max-w-md jobsite:flex-col jobsite:items-center jobsite:gap-5 jobsite:rounded-lg jobsite:border jobsite:border-white/15 jobsite:bg-white/5 jobsite:px-6 jobsite:py-8 jobsite:text-center jobsite:backdrop-blur">
      <p className="jobsite:text-sm jobsite:text-white/70">{endOfGroupText(group)}</p>
      <button
        type="button"
        onClick={onView}
        className="jobsite:flex jobsite:w-full jobsite:items-center jobsite:gap-4 jobsite:rounded-lg jobsite:bg-white/10 jobsite:p-3 jobsite:text-left jobsite:transition-colors jobsite:hover:bg-white/20"
      >
        {cover ? (
          <img
            src={cover}
            alt=""
            loading="lazy"
            className="jobsite:h-16 jobsite:w-24 jobsite:shrink-0 jobsite:rounded-md jobsite:object-cover"
          />
        ) : null}
        <span className="jobsite:min-w-0 jobsite:flex-1">
          <span className="jobsite:block jobsite:text-[11px] jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:text-white/60">
            {nextGroup.kind === "set" && nextGroup.label ? "Next project" : "Next"}
          </span>
          <span className="jobsite:block jobsite:truncate jobsite:font-heading jobsite:text-lg jobsite:text-white">
            {groupTitle(nextGroup)}
          </span>
          <span className="jobsite:block jobsite:text-sm jobsite:text-white/70">
            {photoCountText(nextGroup.stops.length)}
          </span>
        </span>
        <ChevronRight className="jobsite:h-5 jobsite:w-5 jobsite:shrink-0 jobsite:text-white/70" />
      </button>
    </div>
  );
}

export const Gallery: React.FC<GalleryProps> = ({
  heading,
  subheading,
  images: imagesProp,
  layout = "grid",
  variant = "cards",
  columns = "3",
  showCaptions = "yes",
  showTags = "yes",
  backgroundColor,
  puckSiteId,
  puckApiUrl,
  className,
}) => {
  // Computed fresh every render on purpose: the editors can hand back the
  // same array reference with different contents, so memoizing on it made
  // newly added projects invisible until an unrelated change re-rendered.
  const rawImages = Array.isArray(imagesProp) ? imagesProp : [];

  // Live photos for synced projects. Metadata props win in the editors
  // (window config is set after paint there); window config carries the ids
  // on live sites.
  const windowConfig =
    typeof window !== "undefined"
      ? (window as { __SERVICEPRO_CONFIG__?: { siteId?: string; apiUrl?: string } })
          .__SERVICEPRO_CONFIG__
      : undefined;
  const siteId = puckSiteId || windowConfig?.siteId;
  const apiUrl = puckApiUrl || windowConfig?.apiUrl;
  const syncedProjectIds = [
    ...new Set(
      rawImages.map((image) => image?.sourceProjectId).filter((id): id is string => !!id),
    ),
  ].sort();
  // The effect keys on the joined string, not the array: the id list is
  // rebuilt every render, and an array dep would re-run the effect each time.
  const syncedProjectsKey = syncedProjectIds.join(",");
  const [pulledPhotos, setPulledPhotos] = React.useState<Record<string, GalleryPhoto[]>>({});

  React.useEffect(() => {
    const projectIds = syncedProjectsKey ? syncedProjectsKey.split(",") : [];
    if (!siteId || !apiUrl || projectIds.length === 0) {
      return;
    }
    let cancelled = false;
    for (const projectId of projectIds) {
      const cached = projectPhotosCache.get(projectId);
      if (cached && Date.now() - cached.timestamp < PROJECT_PHOTOS_CACHE_TTL) {
        setPulledPhotos((previous) =>
          previous[projectId] ? previous : { ...previous, [projectId]: cached.photos },
        );
        continue;
      }
      fetch(`${apiUrl}/api/sites/public/${siteId}/projects/${projectId}/photos`)
        .then((response) => (response.ok ? response.json() : null))
        .then((data: { photos?: Array<{ url: string; altText: string | null }> } | null) => {
          if (cancelled || !data?.photos) {
            return;
          }
          const photos = data.photos.map((photo) => ({
            imageUrl: photo.url,
            alt: photo.altText ?? undefined,
          }));
          projectPhotosCache.set(projectId, { photos, timestamp: Date.now() });
          setPulledPhotos((previous) => ({ ...previous, [projectId]: photos }));
        })
        .catch(() => {
          // A failed pull leaves the item rendering its stored data.
        });
    }
    return () => {
      cancelled = true;
    };
  }, [siteId, apiUrl, syncedProjectsKey]);

  const images = rawImages
    .map((image) => {
      const pulled = image?.sourceProjectId ? pulledPhotos[image.sourceProjectId] : undefined;
      if (!pulled || pulled.length === 0) {
        return image;
      }
      // The pulled list is the project's truth while synced: first photo
      // covers the tile unless the pro chose a cover, the rest page through
      // the lightbox. Stored photos are ignored, not touched.
      const cover = image.imageUrl || pulled[0].imageUrl;
      return {
        ...image,
        imageUrl: cover,
        photos: pulled.filter((photo) => photo.imageUrl !== cover),
      };
    })
    .filter((image) => image?.imageUrl || image?.beforeImageUrl || image?.afterImageUrl);
  /**
   * Index into `views` — every group's photos in order, each closed by the card
   * that names the next group. Paging is scoped to the open group, so a
   * visitor walking one project never lands in another project's photos.
   */
  const [selected, setSelected] = React.useState<number | null>(null);
  const thumbnailStripRef = React.useRef<HTMLDivElement>(null);
  const hasImages = images.length > 0;
  const isBeforeAfterLayout = layout === "before-after";
  const isMasonryLayout = layout === "masonry";
  // Editorial drops the card chrome: no border, no shadow, no lift — wider gutters and
  // quiet captions under the photo let the photography carry the section.
  const isEditorial = variant === "editorial" && !isBeforeAfterLayout;
  const groups = photoGroupsOf(images);
  const views = galleryViewsOf(groups);
  const selectedView = selected !== null ? (views[selected] ?? null) : null;
  const selectedGroup = selectedView ? (groups[selectedView.groupIndex] ?? null) : null;
  const selectedStop =
    selectedView?.kind === "photo"
      ? (selectedGroup?.stops[selectedView.stopIndex] ?? null)
      : null;
  const selectedImage = selectedStop ? (images[selectedStop.imageIndex] ?? null) : null;
  const selectedPhoto = selectedStop?.photo ?? null;
  const nextGroup =
    selectedView?.kind === "end" ? (groups[selectedView.nextGroupIndex] ?? null) : null;
  // Per-photo caption when the pro wrote one, otherwise the project's own caption.
  const selectedPhotoCaption = selectedPhoto?.caption || selectedImage?.caption || "";
  // The toggles govern the lightbox too. They used to apply only to the tiles, so turning
  // captions off still showed them the moment a visitor opened a photo.
  const showsLightboxCaption = showCaptions === "yes" && !!selectedPhotoCaption;
  const showsLightboxTag = showTags === "yes" && !!selectedImage?.tag;
  const surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const;
  // The counter and the thumbnail strip describe the OPEN group, not the
  // section: "3 / 8" of a project a visitor can see the whole of.
  const groupStopCount = selectedGroup?.stops.length ?? 0;

  const openImage = (index: number) => {
    // The tile's own cover is where its group opens.
    const view = viewIndexForImage(views, groups, index);
    if (view === -1) return;
    setSelected(view);
  };

  const viewCount = views.length;
  const moveSelection = React.useCallback(
    (direction: -1 | 1) => {
      setSelected((previous) => {
        if (previous === null || viewCount === 0) return previous;
        return (previous + direction + viewCount) % viewCount;
      });
    },
    [viewCount],
  );

  // Arrow keys page through the section. Escape is Radix's job.
  React.useEffect(() => {
    if (selected === null) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        moveSelection(-1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        moveSelection(1);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selected, moveSelection]);

  // Keep the active thumbnail in view when paging with the arrows or the keyboard.
  React.useEffect(() => {
    thumbnailStripRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [selected]);

  return (
    <>
      <section
        className={cn(
          "jobsite:py-16 jobsite:md:py-24",
          backgroundColor ? "" : "jobsite:bg-background",
          className,
        )}
        style={{ backgroundColor: backgroundColor || undefined }}
      >
        <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
          <div className="jobsite:mx-auto jobsite:mb-12 jobsite:max-w-3xl jobsite:text-center">
            <h2 className="jobsite:font-heading jobsite:text-3xl jobsite:tracking-tight jobsite:text-balance jobsite:text-foreground jobsite:sm:text-4xl jobsite:md:text-5xl">
              {heading}
            </h2>
            {subheading ? (
              <p className="jobsite:mt-4 jobsite:text-lg jobsite:text-pretty jobsite:text-muted-foreground jobsite:md:text-xl">
                {subheading}
              </p>
            ) : null}
          </div>

          {hasImages ? (
            <div
              className={cn(
                isEditorial ? "jobsite:gap-6 jobsite:md:gap-8" : "jobsite:gap-4 jobsite:md:gap-5",
                isMasonryLayout
                  ? getMasonryColumnsClass(columns)
                  : cn("jobsite:grid", getGridCols(columns)),
              )}
            >
              {images.map((image, index) => {
                const imageKey =
                  image.afterImageUrl ||
                  image.beforeImageUrl ||
                  image.imageUrl ||
                  image.alt ||
                  String(index);

                if (isBeforeAfterLayout) {
                  return (
                    <BeforeAfterGalleryCard
                      key={`${imageKey}-${index}`}
                      heading={heading}
                      image={image}
                      showCaptions={showCaptions}
                      showTags={showTags}
                      surfaceRadiusStyle={surfaceRadiusStyle}
                    />
                  );
                }

                const isShowcaseLayout = layout === "showcase";
                const isFeatured = isShowcaseLayout && index === 0;
                // The featured tile spans rows 1-2, leaving (columns - 2) * 2
                // sibling slots beside it; everything after lands in row 3+.
                const isShowcaseOverflow =
                  isShowcaseLayout &&
                  (columns === "3" || columns === "4") &&
                  index >= (columns === "4" ? 5 : 3);
                const displayImageUrl =
                  image.imageUrl || image.afterImageUrl || image.beforeImageUrl;
                const imageCardClassName = cn(
                  "jobsite:group jobsite:relative jobsite:block jobsite:w-full jobsite:overflow-hidden jobsite:bg-background jobsite:text-left",
                  // No lift-and-drop-shadow on hover: the photo is the subject,
                  // and a card jumping toward the cursor reads as a widget. The
                  // border tint plus the image's own slow zoom is the whole
                  // hover gesture.
                  !isEditorial &&
                    "jobsite:border jobsite:border-border/70 jobsite:shadow-sm jobsite:transition-colors jobsite:duration-200 jobsite:hover:border-primary/25",
                  isFeatured && getShowcaseFeaturedSpan(columns),
                  // Fill the stretched grid cell so the media inside has a definite height to
                  // resolve `h-full` against. Overflow tiles skip it: their row is auto-height,
                  // and a percentage against it is the cycle the WebKit caveat above warns about.
                  isShowcaseLayout && !isShowcaseOverflow && "jobsite:lg:h-full",
                  // Multi-column flow, not grid: keep tiles whole and space them vertically.
                  isMasonryLayout &&
                    (isEditorial
                      ? "jobsite:mb-6 jobsite:break-inside-avoid jobsite:md:mb-8"
                      : "jobsite:mb-4 jobsite:break-inside-avoid jobsite:md:mb-5"),
                );
                const hasCaptionText =
                  (showTags === "yes" && !!image.tag) || (showCaptions === "yes" && !!image.caption);
                const hasOverlayText = hasCaptionText && !isEditorial;
                const photoCount = getProjectPhotos(image).length;
                const imageCardContent = (
                  <>
                    <div
                      className={cn(
                        "jobsite:relative jobsite:overflow-hidden",
                        // Masonry keeps the photo's own proportions; every other layout crops
                        // to a shared ratio so the grid stays even.
                        isMasonryLayout
                          ? ""
                          : isShowcaseLayout
                            ? getShowcaseMediaClass(columns, isFeatured, isShowcaseOverflow)
                            : "jobsite:aspect-[4/3]",
                      )}
                    >
                      <img
                        src={displayImageUrl}
                        alt={image.alt || image.caption || plainText(heading)}
                        style={imagePositionStyle(image.position)}
                        className={cn(
                          "jobsite:w-full jobsite:transition-transform jobsite:duration-500 jobsite:group-hover:scale-[1.03]",
                          isMasonryLayout
                            ? "jobsite:h-auto"
                            : isShowcaseLayout
                              ? getShowcaseImageClass(columns, isFeatured, isShowcaseOverflow)
                              : "jobsite:h-full jobsite:object-cover",
                        )}
                        loading="lazy"
                      />
                      {photoCount > 1 ? (
                        <span
                          className="jobsite:absolute jobsite:right-3 jobsite:top-3 jobsite:flex jobsite:items-center jobsite:gap-1.5 jobsite:bg-background/80 jobsite:px-2.5 jobsite:py-1 jobsite:text-[11px] jobsite:font-semibold jobsite:text-foreground jobsite:backdrop-blur"
                          style={surfaceRadiusStyle}
                        >
                          <Images className="jobsite:h-3.5 jobsite:w-3.5" />
                          {photoCount}
                          <span className="jobsite:sr-only">photos in this project</span>
                        </span>
                      ) : null}
                      {hasComparison(image) ? (
                        <span
                          className="jobsite:absolute jobsite:left-3 jobsite:top-3 jobsite:bg-background/80 jobsite:px-2.5 jobsite:py-1 jobsite:text-[11px] jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.14em] jobsite:text-foreground jobsite:backdrop-blur"
                          style={surfaceRadiusStyle}
                        >
                          {image.beforeLabel || "Before"} / {image.afterLabel || "After"}
                        </span>
                      ) : null}
                      {hasOverlayText ? (
                        <div className="jobsite:absolute jobsite:inset-x-0 jobsite:bottom-0 jobsite:h-2/5 jobsite:bg-linear-to-t jobsite:from-black/80 jobsite:via-black/40 jobsite:to-transparent" />
                      ) : null}
                      {hasOverlayText ? (
                        <div className="jobsite:absolute jobsite:inset-x-0 jobsite:bottom-0 jobsite:flex jobsite:flex-col jobsite:gap-2 jobsite:p-4 jobsite:sm:p-5">
                          {showTags === "yes" && image.tag ? (
                            <span
                              className="jobsite:w-fit jobsite:bg-background/80 jobsite:px-3 jobsite:py-1 jobsite:text-[11px] jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:text-accent jobsite:backdrop-blur"
                              style={surfaceRadiusStyle}
                            >
                              {image.tag}
                            </span>
                          ) : null}
                          {showCaptions === "yes" && image.caption ? (
                            <div className="jobsite:max-w-2xl jobsite:font-heading jobsite:text-lg jobsite:text-white jobsite:[text-shadow:0_1px_10px_rgba(0,0,0,0.45)] jobsite:sm:text-xl">
                              {image.caption}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    {isEditorial && hasCaptionText ? (
                      <div className="jobsite:flex jobsite:flex-col jobsite:gap-1 jobsite:pt-3">
                        {showTags === "yes" && image.tag ? (
                          <span className="jobsite:text-[11px] jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:text-muted-foreground">
                            {image.tag}
                          </span>
                        ) : null}
                        {showCaptions === "yes" && image.caption ? (
                          <div className="jobsite:font-heading jobsite:text-base jobsite:text-foreground">
                            {image.caption}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                );

                if (opensLink(image)) {
                  return (
                    <SmartLink
                      key={`${imageKey}-${index}`}
                      to={image.href}
                      className={imageCardClassName}
                      style={surfaceRadiusStyle}
                    >
                      {imageCardContent}
                    </SmartLink>
                  );
                }

                return (
                  <button
                    key={`${imageKey}-${index}`}
                    type="button"
                    onClick={() => openImage(index)}
                    className={imageCardClassName}
                    style={surfaceRadiusStyle}
                  >
                    {imageCardContent}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="jobsite:flex jobsite:min-h-[320px] jobsite:flex-col jobsite:items-center jobsite:justify-center jobsite:rounded-[calc(var(--jobsite-radius)+8px)] jobsite:border jobsite:border-dashed jobsite:border-border jobsite:bg-muted/30 jobsite:px-6 jobsite:text-center">
              <div className="jobsite:mb-4 jobsite:flex jobsite:h-14 jobsite:w-14 jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:bg-background jobsite:text-muted-foreground">
                <ImageIcon className="jobsite:h-7 jobsite:w-7" />
              </div>
              <h3 className="jobsite:font-heading jobsite:text-xl jobsite:text-foreground">
                Add project photos
              </h3>
              <p className="jobsite:mt-2 jobsite:max-w-md jobsite:text-sm jobsite:text-muted-foreground">
                Upload before-and-after shots, recent installs, or finished work to turn this
                section into visual proof.
              </p>
            </div>
          )}
        </div>
      </section>

      <Dialog
        open={!isBeforeAfterLayout && selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <DialogContent
          showCloseButton={false}
          overlayClassName="jobsite:bg-black/85"
          className="jobsite:border-none jobsite:bg-transparent jobsite:p-0 jobsite:shadow-none jobsite:sm:max-w-[95vw]!"
        >
          {selectedView ? (
            // A photo viewer, not a card: chrome stays dark and out of the way at every site
            // theme so the photo is the only lit thing on screen.
            <div className="jobsite:relative jobsite:mx-auto jobsite:flex jobsite:w-full jobsite:min-w-0 jobsite:max-w-6xl jobsite:flex-col jobsite:items-center jobsite:gap-4">
              {/*
                The stage is a fixed box and the photo is centred inside it. Anchoring the
                controls to the photo instead would move them every time a portrait shot
                followed a landscape one, so paging through a project meant chasing the
                arrows around the screen.
              */}
              <div className="jobsite:relative jobsite:flex jobsite:h-[68vh] jobsite:w-full jobsite:min-w-0 jobsite:items-center jobsite:justify-center">
                {selectedView.kind === "end" && selectedGroup && nextGroup ? (
                  <NextGroupCard
                    group={selectedGroup}
                    nextGroup={nextGroup}
                    onView={() => moveSelection(1)}
                  />
                ) : selectedImage && selectedPhoto ? (
                  selectedStop?.photoIndex === 0 && hasComparison(selectedImage) ? (
                    // The cover of a comparison project is the comparison
                    // itself: the same frame, interactive, in place of a flat
                    // photo. Paging goes on to the project's other photos.
                    <div className="jobsite:max-h-full jobsite:w-full jobsite:max-w-4xl jobsite:overflow-hidden jobsite:rounded-lg jobsite:shadow-2xl">
                      <ComparisonFrame
                        beforeSrc={selectedImage.beforeImageUrl as string}
                        afterSrc={selectedImage.afterImageUrl as string}
                        beforeAlt={`${selectedImage.beforeLabel || "Before"}: ${selectedImage.alt || selectedPhotoCaption || plainText(heading)}`}
                        afterAlt={`${selectedImage.afterLabel || "After"}: ${selectedImage.alt || selectedPhotoCaption || plainText(heading)}`}
                        beforeLabel={selectedImage.beforeLabel || "Before"}
                        afterLabel={selectedImage.afterLabel || "After"}
                        groupLabel={`Compare ${selectedImage.beforeLabel || "Before"} and ${selectedImage.afterLabel || "After"}${selectedImage.caption ? `: ${selectedImage.caption}` : ""}`}
                        eager
                      />
                    </div>
                  ) : (
                    <img
                      src={selectedPhoto.imageUrl}
                      alt={
                        selectedPhoto.alt ||
                        selectedImage.alt ||
                        selectedPhotoCaption ||
                        plainText(heading)
                      }
                      className="jobsite:max-h-full jobsite:max-w-full jobsite:rounded-lg jobsite:object-contain jobsite:shadow-2xl"
                    />
                  )
                ) : null}
                <DialogClose className="jobsite:absolute jobsite:right-0 jobsite:top-0 jobsite:flex jobsite:h-10 jobsite:w-10 jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:bg-black/55 jobsite:text-white jobsite:backdrop-blur jobsite:transition-colors jobsite:hover:bg-black/75">
                  <X className="jobsite:h-4 jobsite:w-4" />
                  <span className="jobsite:sr-only">Close</span>
                </DialogClose>
                {viewCount > 1 ? (
                  <>
                    <button
                      type="button"
                      onClick={() => moveSelection(-1)}
                      aria-label="Previous photo"
                      className="jobsite:absolute jobsite:left-0 jobsite:top-1/2 jobsite:flex jobsite:h-11 jobsite:w-11 jobsite:-translate-y-1/2 jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:bg-black/55 jobsite:text-white jobsite:backdrop-blur jobsite:transition-colors jobsite:hover:bg-black/75"
                    >
                      <ChevronLeft className="jobsite:h-5 jobsite:w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveSelection(1)}
                      aria-label={
                        selectedView.kind === "end" && nextGroup
                          ? `View ${nextGroup.label}`
                          : "Next photo"
                      }
                      className="jobsite:absolute jobsite:right-0 jobsite:top-1/2 jobsite:flex jobsite:h-11 jobsite:w-11 jobsite:-translate-y-1/2 jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:bg-black/55 jobsite:text-white jobsite:backdrop-blur jobsite:transition-colors jobsite:hover:bg-black/75"
                    >
                      <ChevronRight className="jobsite:h-5 jobsite:w-5" />
                    </button>
                  </>
                ) : null}
              </div>

              {selectedView.kind === "photo" &&
              (showsLightboxCaption || showsLightboxTag || groupStopCount > 1) ? (
                <div className="jobsite:flex jobsite:w-full jobsite:flex-wrap jobsite:items-center jobsite:justify-center jobsite:gap-x-3 jobsite:gap-y-2 jobsite:text-center">
                  {showsLightboxTag ? (
                    <span
                      className="jobsite:bg-white/15 jobsite:px-3 jobsite:py-1 jobsite:text-[11px] jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:text-white"
                      style={surfaceRadiusStyle}
                    >
                      {selectedImage?.tag}
                    </span>
                  ) : null}
                  {showsLightboxCaption ? (
                    <span className="jobsite:font-heading jobsite:text-lg jobsite:text-white">
                      {selectedPhotoCaption}
                    </span>
                  ) : null}
                  {groupStopCount > 1 ? (
                    <span className="jobsite:text-sm jobsite:tabular-nums jobsite:text-white/70">
                      {selectedView.stopIndex + 1} / {groupStopCount}
                      {/* The project's name earns its place next to the count:
                          it is what tells a visitor which set they are inside. */}
                      {selectedGroup?.kind === "set" ? (
                        <span className="jobsite:ml-2">{selectedGroup.label}</span>
                      ) : null}
                    </span>
                  ) : null}
                </div>
              ) : null}

              {selectedGroup && groupStopCount > 1 ? (
                // The scroll container stays full width; the track inside is content-width and
                // auto-margined, so a few thumbs centre while many still scroll. Putting
                // justify-center on the scroll container itself would centre them but clip
                // the first ones out of reach once they overflow.
                <div
                  ref={thumbnailStripRef}
                  className="jobsite:w-full jobsite:min-w-0 jobsite:overflow-x-auto jobsite:px-1 jobsite:pb-1"
                >
                  <div className="jobsite:mx-auto jobsite:flex jobsite:w-max jobsite:gap-2">
                  {selectedGroup.stops.map(({ photo }, stopIndex) => {
                    const isActive =
                      selectedView.kind === "photo" && stopIndex === selectedView.stopIndex;

                    return (
                      <button
                        key={`${photo.imageUrl}-${stopIndex}`}
                        type="button"
                        data-active={isActive}
                        aria-label={`Photo ${stopIndex + 1} of ${groupStopCount}`}
                        aria-current={isActive}
                        onClick={() => {
                          const view = viewIndexForStop(
                            views,
                            selectedView.groupIndex,
                            stopIndex,
                          );
                          if (view !== -1) setSelected(view);
                        }}
                        className={cn(
                          "jobsite:h-14 jobsite:w-20 jobsite:shrink-0 jobsite:overflow-hidden jobsite:rounded-md jobsite:transition-opacity",
                          isActive
                            ? "jobsite:opacity-100 jobsite:ring-2 jobsite:ring-white"
                            : "jobsite:opacity-50 jobsite:hover:opacity-80",
                        )}
                      >
                        <img
                          src={photo.imageUrl}
                          alt=""
                          loading="lazy"
                          className="jobsite:h-full jobsite:w-full jobsite:object-cover"
                        />
                      </button>
                    );
                  })}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};
